import * as React from "react"
import type { ComponentProps } from "react"
import { ArrowDownIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

type ConversationContextValue = {
  containerRef: React.RefObject<HTMLDivElement | null>
  isAtBottom: boolean
  scrollToBottom: (behavior?: ScrollBehavior) => void
}

const ConversationContext = React.createContext<ConversationContextValue | null>(
  null
)

export function useConversationContext() {
  const ctx = React.useContext(ConversationContext)
  if (!ctx) {
    throw new Error("ConversationScrollButton must be used within <Conversation>")
  }
  return ctx
}

export type ConversationProps = ComponentProps<"div">

export const Conversation = ({ className, children, ...props }: ConversationProps) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const bottomSentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [isAtBottom, setIsAtBottom] = React.useState(true)
  const shouldStickRef = React.useRef(true)

  const computeIsAtBottom = React.useCallback(() => {
    const el = containerRef.current
    if (!el) return true
    const threshold = 240
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold
  }, [])

  const scrollToBottom = React.useCallback(
    (behavior: ScrollBehavior = "smooth") => {
      const el = containerRef.current
      if (!el) return
      shouldStickRef.current = true
      if (behavior === "smooth") {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
      } else {
        el.scrollTop = el.scrollHeight
      }
      setIsAtBottom(true)
    },
    []
  )

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const onScroll = () => {
      const atBottom = computeIsAtBottom()
      shouldStickRef.current = atBottom
      setIsAtBottom(atBottom)
    }

    onScroll()
    el.addEventListener("scroll", onScroll, { passive: true })
    return () => el.removeEventListener("scroll", onScroll)
  }, [computeIsAtBottom])

  React.useEffect(() => {
    const el = containerRef.current
    const sentinel = bottomSentinelRef.current
    if (!el || !sentinel) return
    if (typeof IntersectionObserver === "undefined") return

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        const atBottom = entry.isIntersecting
        shouldStickRef.current = atBottom
        setIsAtBottom(atBottom)
      },
      {
        root: el,
        threshold: 0.01,
        rootMargin: "0px 0px 240px 0px",
      }
    )

    io.observe(sentinel)
    return () => io.disconnect()
  }, [])

  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const content = el.querySelector(
      "[data-conversation-content]"
    ) as HTMLElement | null
    if (!content) return

    const onContentChange = () => {
      if (shouldStickRef.current) {
        el.scrollTop = el.scrollHeight
        setIsAtBottom(true)
        return
      }
      setIsAtBottom(computeIsAtBottom())
    }

    onContentChange()

    const mo = new MutationObserver(onContentChange)
    mo.observe(content, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    const ro = new ResizeObserver(onContentChange)
    ro.observe(content)

    return () => {
      mo.disconnect()
      ro.disconnect()
    }
  }, [computeIsAtBottom])

  const value = React.useMemo(
    () => ({ containerRef, isAtBottom, scrollToBottom }),
    [isAtBottom, scrollToBottom]
  )

  return (
    <ConversationContext.Provider value={value}>
      <div
        ref={containerRef}
        className={cn("relative flex-1 overflow-y-auto", className)}
        role="log"
        {...props}
      >
        {children}
        <div ref={bottomSentinelRef} className="h-px w-full" aria-hidden="true" />
      </div>
    </ConversationContext.Provider>
  )
}

export type ConversationContentProps = ComponentProps<"div">

export const ConversationContent = ({
  className,
  ...props
}: ConversationContentProps) => (
  <div
    data-conversation-content
    className={cn("p-4", className)}
    {...props}
  />
)

export type ConversationEmptyStateProps = Omit<ComponentProps<"div">, "title"> & {
  title?: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
}

export const ConversationEmptyState = ({
  className,
  title = "No messages yet",
  description = "Start a conversation to see messages here",
  icon,
  children,
  ...props
}: ConversationEmptyStateProps) => (
  <div
    className={cn(
      "flex size-full flex-col items-center justify-center gap-3 p-8 text-center",
      className
    )}
    {...props}
  >
    {children ?? (
      <>
        {icon && <div className="text-muted-foreground">{icon}</div>}
        <div className="space-y-1">
          <h3 className="text-sm font-medium">{title}</h3>
          {description && (
            <p className="text-muted-foreground text-sm">{description}</p>
          )}
        </div>
      </>
    )}
  </div>
)

export type ConversationScrollButtonProps = ComponentProps<typeof Button>

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useConversationContext()

  const [present, setPresent] = React.useState(false)
  const [phase, setPhase] = React.useState<"in" | "out">("out")

  React.useEffect(() => {
    if (!isAtBottom) {
      setPresent(true)
      setPhase("in")
      return
    }

    if (!present) return
    setPhase("out")
    const t = window.setTimeout(() => setPresent(false), 200)
    return () => window.clearTimeout(t)
  }, [isAtBottom, present])

  if (!present) return null

  return (
    <div className="pointer-events-none sticky bottom-4 left-0 right-0 z-10 flex justify-center">
      <Button
        className={cn(
          "pointer-events-auto rounded-full",
          "duration-200 will-change-transform",
          phase === "in" ? "animate-in zoom-in-95 ease-out" : "animate-out zoom-out-95 ease-in",
          "bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/65 hover:bg-background",
          className
        )}
        onClick={() => scrollToBottom("smooth")}
        size="icon"
        type="button"
        variant="outline"
        {...props}
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    </div>
  )
}
